import { useParams, Navigate } from "react-router-dom";
import { useQuery } from "@apollo/client/react";
import { Paper } from "@mantine/core";
import { Alert } from "design-system/src/components/alert";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { FeatureToggle } from "shared/src/constants/feature-toggle-names";
import {
  Condition,
  Medication,
} from "shared/src/data/medication-condition-pairs";
import { gql } from "@/__generated__";
import Navbar from "@/components/layout/navbar/navbar";
import { ReferPatientProgressTracker } from "@/components/refer-patient-progress-tracker";
import { StrengthenReferral } from "../strengthen-referral/strengthen-referral";
import PageLoader from "@/components/page-loader/page-loader";
import { PageError } from "@/components/page-error/page-error";
import { LetterOfMedicalNecessityContent } from "./letter-of-medical-necessity-content";
import tabContentStyles from "../strengthen-referral/styles/tab-content.module.scss";

const GET_APPEAL_DATA_FOR_LETTER_OF_MEDICAL_NECESSITY_PAGE = gql(`
  query GetAppealDataForLetterOfMedicalNecessityPage($appealId: ID!) {
    getAppealById(appealId: $appealId) {
      appealId
      denial {
        condition
        medicationBrandName
      }
    }
  }
`);

export function LetterOfMedicalNecessityPage() {
  const { appealId } = useParams<{ appealId: string }>();
  const letterOfMedicalNecessityEnabled = useFeatureFlagEnabled(
    FeatureToggle.LETTER_OF_MEDICAL_NECESSITY_GENERATION,
  );

  const { data, loading, error } = useQuery(
    GET_APPEAL_DATA_FOR_LETTER_OF_MEDICAL_NECESSITY_PAGE,
    {
      variables: { appealId: appealId! },
      fetchPolicy: "no-cache",
      skip: !appealId || letterOfMedicalNecessityEnabled !== true,
    },
  );

  if (!appealId) {
    return null;
  }

  if (letterOfMedicalNecessityEnabled === undefined) {
    return (
      <Navbar
        titleComponent={<ReferPatientProgressTracker appealId={appealId} />}
      >
        <PageLoader />
      </Navbar>
    );
  }

  if (letterOfMedicalNecessityEnabled === false) {
    return <Navigate to={`/appeals/${appealId}/strengthen/patient`} replace />;
  }

  if (loading) {
    return (
      <Navbar
        titleComponent={<ReferPatientProgressTracker appealId={appealId} />}
      >
        <PageLoader />
      </Navbar>
    );
  }

  if (error || !data?.getAppealById) {
    return (
      <Navbar
        titleComponent={<ReferPatientProgressTracker appealId={appealId} />}
      >
        <PageError />
      </Navbar>
    );
  }

  const condition = data.getAppealById.denial?.condition;
  const medication = data.getAppealById.denial?.medicationBrandName;
  const isHppStrensiq =
    condition === Condition.HYPOPHOSPHATASIA &&
    medication === Medication.STRENSIQ;

  if (!isHppStrensiq) {
    return (
      <Navbar
        titleComponent={<ReferPatientProgressTracker appealId={appealId} />}
      >
        <StrengthenReferral
          appealId={appealId}
          activeTab="letter-of-medical-necessity"
          tabContent={
            <Paper p="xl" shadow="sm" withBorder>
              <Alert variant="error">
                <Alert.Title>Not supported</Alert.Title>
                <Alert.Caption>
                  Letter of Medical Necessity generation is currently only
                  supported for HPP/Strensiq cases.
                </Alert.Caption>
              </Alert>
            </Paper>
          }
        />
      </Navbar>
    );
  }

  return (
    <Navbar
      titleComponent={<ReferPatientProgressTracker appealId={appealId} />}
    >
      <StrengthenReferral
        appealId={appealId}
        activeTab="letter-of-medical-necessity"
        tabContent={
          <Paper className={tabContentStyles.container} p="xl" shadow="sm">
            <LetterOfMedicalNecessityContent appealId={appealId} />
          </Paper>
        }
      />
    </Navbar>
  );
}
